import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import { 
  formatCurrency, 
  formatDate, 
  formatDateTime, 
  getDaysUntilExpiry, 
  calculateCommitmentPercentage,
  calculateMonthlyPayment
  // calculateTotalInterest // Unused import
} from '../../utils/helpers';
import LtvBadge from '../molecules/LtvBadge';
import EncumbranceBadge from '../molecules/EncumbranceBadge';
import './assetdetail.css';

const AssetDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { state, actions } = useDemo();
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [commitAmount, setCommitAmount] = useState('');

  const listing = state.listings.find(l => l.id === id);
  const property = state.properties.find(p => p.id === listing?.assetId);
  const commitments = state.commitments.filter(c => c.listingId === id);
  const investors = state.investors;

  if (!listing || !property) {
    return (
      <div className="jo-xyz-demo-asset-detail-page">
        <div className="jo-xyz-demo-container">
          <div className="jo-xyz-demo-error-state">
            <h1>Listing Not Found</h1>
            <p>The requested listing could not be found.</p>
            <button 
              className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
              onClick={() => history.push('/jo-xyz-demo/marketplace')}
            >
              Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilExpiry = getDaysUntilExpiry(listing.expiresAt);
  const commitmentPercentage = calculateCommitmentPercentage(listing.committedAmount, listing.requestedAmount);
  const isExpired = daysUntilExpiry <= 0;
  const isFullyFunded = listing.status === 'Matched';
  const remainingAmount = listing.requestedAmount - listing.committedAmount;

  const handleCommit = () => {
    const amount = parseFloat(commitAmount);
    if (amount > 0 && amount <= remainingAmount) {
      const commitment = {
        id: `cmt-${Date.now()}`,
        listingId: listing.id,
        investorId: state.currentInvestor?.id || 'demo-investor',
        amount: amount,
        status: 'Committed',
        createdAt: new Date().toISOString(),
        notes: 'Demo commitment',
        terms: {
          proposedRate: 8.5,
          proposedTerm: listing.desiredTermMonths,
          conditions: ['Standard terms apply']
        }
      };

      actions.commitToListing(commitment);
      actions.showToast(`Commitment of ${formatCurrency(amount)} recorded successfully!`);
      setShowCommitModal(false);
      setCommitAmount('');
    }
  };

  const getInvestorName = (investorId) => {
    const investor = investors.find(i => i.id === investorId);
    return investor?.name || 'Unknown Investor';
  };

  const getStatusColor = () => {
    switch (listing.status) {
      case 'Open':
        return isExpired ? '#EF4444' : '#6EE7F2';
      case 'Matched':
        return '#22C55E';
      case 'Closed':
        return '#A3B2C5';
      default:
        return '#F59E0B';
    }
  };

  return (
    <div className="jo-xyz-demo-asset-detail-page">
      <div className="jo-xyz-demo-asset-detail-page__header">
        <button 
          className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
          onClick={() => history.goBack()}
        >
          ← Back
        </button>
        <h1>{listing.title}</h1>
        <div className="jo-xyz-demo-asset-detail-page__status">
          <span 
            className="jo-xyz-demo-status-badge"
            style={{ backgroundColor: getStatusColor() }}
          >
            {listing.status}
          </span>
        </div>
      </div>

      <div className="jo-xyz-demo-asset-detail-page__content">
        <div className="jo-xyz-demo-asset-detail-page__left">
          {/* Property Information */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Property Information</h3>
              </div>
              <div className="jo-xyz-demo-property-info">
                <div className="jo-xyz-demo-property-info__address">
                  <h4>📍 {property.address}</h4>
                </div>
                <div className="jo-xyz-demo-property-info__grid">
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">Valuation</span>
                    <span className="jo-xyz-demo-info-value">{formatCurrency(property.valuation)}</span>
                  </div>
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">Cap Rate</span>
                    <span className="jo-xyz-demo-info-value">{(property.capRate * 100).toFixed(1)}%</span>
                  </div>
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">NOI</span>
                    <span className="jo-xyz-demo-info-value">{formatCurrency(property.noi)}</span>
                  </div>
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">Square Footage</span>
                    <span className="jo-xyz-demo-info-value">{property.squareFootage.toLocaleString()} sq ft</span>
                  </div>
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">Year Built</span>
                    <span className="jo-xyz-demo-info-value">{property.yearBuilt}</span>
                  </div>
                  <div className="jo-xyz-demo-info-item">
                    <span className="jo-xyz-demo-info-label">Occupancy Rate</span>
                    <span className="jo-xyz-demo-info-value">{(property.occupancyRate * 100).toFixed(0)}%</span>
                  </div>
                </div>
                <div className="jo-xyz-demo-property-info__notes">
                  <h5>Property Notes</h5>
                  <p>{property.notes}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Loan Terms */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Loan Terms</h3>
              </div>
              <div className="jo-xyz-demo-loan-terms">
                <div className="jo-xyz-demo-loan-terms__grid">
                  <div className="jo-xyz-demo-term-item">
                    <span className="jo-xyz-demo-term-label">Requested Amount</span>
                    <span className="jo-xyz-demo-term-value">{formatCurrency(listing.requestedAmount)}</span>
                  </div>
                  <div className="jo-xyz-demo-term-item">
                    <span className="jo-xyz-demo-term-label">Term</span>
                    <span className="jo-xyz-demo-term-value">{listing.desiredTermMonths} months</span>
                  </div>
                  <div className="jo-xyz-demo-term-item">
                    <span className="jo-xyz-demo-term-label">Lien Position</span>
                    <span className="jo-xyz-demo-term-value">{listing.desiredLien}</span>
                  </div>
                  <div className="jo-xyz-demo-term-item">
                    <span className="jo-xyz-demo-term-label">LTV</span>
                    <span className="jo-xyz-demo-term-value">{(listing.ltv * 100).toFixed(1)}%</span>
                  </div>
                </div>
                <div className="jo-xyz-demo-loan-terms__requirements">
                  <h5>Requirements</h5>
                  <ul>
                    {listing.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Commitments */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Investor Commitments</h3>
                <span className="jo-xyz-demo-commitments-count">
                  {commitments.length} commitment{commitments.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="jo-xyz-demo-commitments-list">
                {commitments.length === 0 ? (
                  <div className="jo-xyz-demo-no-commitments">
                    <p>No commitments yet. Be the first to invest!</p>
                  </div>
                ) : (
                  commitments.map((commitment) => (
                    <div key={commitment.id} className="jo-xyz-demo-commitment-item">
                      <div className="jo-xyz-demo-commitment-item__header">
                        <span className="jo-xyz-demo-commitment-investor">
                          {getInvestorName(commitment.investorId)}
                        </span>
                        <span className="jo-xyz-demo-commitment-amount">
                          {formatCurrency(commitment.amount)}
                        </span>
                      </div>
                      <div className="jo-xyz-demo-commitment-item__details">
                        <div className="jo-xyz-demo-commitment-meta">
                          <span>Committed: {formatDateTime(commitment.createdAt)}</span>
                          <span>Status: {commitment.status}</span>
                        </div>
                        {commitment.notes && (
                          <div className="jo-xyz-demo-commitment-notes">
                            <strong>Notes:</strong> {commitment.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="jo-xyz-demo-asset-detail-page__right">
          {/* Funding Progress */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Funding Progress</h3>
              </div>
              <div className="jo-xyz-demo-funding-progress">
                <div className="jo-xyz-demo-progress-circle">
                  <div className="jo-xyz-demo-progress-circle__content">
                    <span className="jo-xyz-demo-progress-percentage">
                      {commitmentPercentage.toFixed(0)}%
                    </span>
                    <span className="jo-xyz-demo-progress-label">Funded</span>
                  </div>
                </div>
                <div className="jo-xyz-demo-progress-details">
                  <div className="jo-xyz-demo-progress-item">
                    <span className="jo-xyz-demo-progress-item__label">Committed</span>
                    <span className="jo-xyz-demo-progress-item__value">
                      {formatCurrency(listing.committedAmount)}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-progress-item">
                    <span className="jo-xyz-demo-progress-item__label">Remaining</span>
                    <span className="jo-xyz-demo-progress-item__value">
                      {formatCurrency(remainingAmount)}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-progress-item">
                    <span className="jo-xyz-demo-progress-item__label">Total Requested</span>
                    <span className="jo-xyz-demo-progress-item__value">
                      {formatCurrency(listing.requestedAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Risk Assessment</h3>
              </div>
              <div className="jo-xyz-demo-risk-assessment">
                <div className="jo-xyz-demo-risk-badges">
                  <LtvBadge ltv={listing.ltv} size="medium" />
                  <EncumbranceBadge 
                    lienPosition={property.lienPosition}
                    encumbrances={property.encumbrances}
                    inUnderwriting={property.inUnderwriting}
                    size="medium"
                  />
                </div>
                <div className="jo-xyz-demo-risk-details">
                  <div className="jo-xyz-demo-risk-item">
                    <span className="jo-xyz-demo-risk-label">Property Eligible</span>
                    <span className={`jo-xyz-demo-risk-value ${property.eligible ? 'jo-xyz-demo-risk-value--good' : 'jo-xyz-demo-risk-value--bad'}`}>
                      {property.eligible ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-risk-item">
                    <span className="jo-xyz-demo-risk-label">In Underwriting</span>
                    <span className={`jo-xyz-demo-risk-value ${property.inUnderwriting ? 'jo-xyz-demo-risk-value--warning' : 'jo-xyz-demo-risk-value--good'}`}>
                      {property.inUnderwriting ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-risk-item">
                    <span className="jo-xyz-demo-risk-label">Last Appraisal</span>
                    <span className="jo-xyz-demo-risk-value">{formatDate(property.lastAppraisalAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Panel */}
          <div className="jo-xyz-demo-asset-detail-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Actions</h3>
              </div>
              <div className="jo-xyz-demo-action-panel">
                {!isExpired && !isFullyFunded && (
                  <button 
                    className="jo-xyz-demo-btn jo-xyz-demo-btn--primary jo-xyz-demo-btn--lg"
                    onClick={() => setShowCommitModal(true)}
                    disabled={!state.currentInvestor}
                  >
                    {state.currentInvestor ? 'Commit to Investment' : 'Sign Up to Invest'}
                  </button>
                )}
                
                {!state.currentInvestor && (
                  <button 
                    className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--lg"
                    onClick={() => history.push('/jo-xyz-demo/investor/signup')}
                  >
                    Create Investor Account
                  </button>
                )}

                <div className="jo-xyz-demo-action-info">
                  <p>
                    {isExpired ? 'This listing has expired.' : 
                     isFullyFunded ? 'This listing is fully funded.' :
                     `Investment opportunity expires in ${daysUntilExpiry} days.`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commit Modal */}
      {showCommitModal && (
        <div className="jo-xyz-demo-modal-overlay" onClick={() => setShowCommitModal(false)}>
          <div className="jo-xyz-demo-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="jo-xyz-demo-modal-header">
              <h3>Commit to Investment</h3>
              <button 
                className="jo-xyz-demo-modal-close"
                onClick={() => setShowCommitModal(false)}
              >
                ×
              </button>
            </div>
            <div className="jo-xyz-demo-modal-body">
              <div className="jo-xyz-demo-commit-form">
                <div className="jo-xyz-demo-form-group">
                  <label>Investment Amount</label>
                  <input
                    type="number"
                    className="jo-xyz-demo-input"
                    value={commitAmount}
                    onChange={(e) => setCommitAmount(e.target.value)}
                    placeholder={`Max: ${formatCurrency(remainingAmount)}`}
                    max={remainingAmount}
                    min="1000"
                    step="1000"
                  />
                  <div className="jo-xyz-demo-form-help">
                    Available: {formatCurrency(remainingAmount)}
                  </div>
                </div>
                <div className="jo-xyz-demo-commit-summary">
                  <h4>Investment Summary</h4>
                  <div className="jo-xyz-demo-summary-item">
                    <span>Amount:</span>
                    <span>{formatCurrency(commitAmount || 0)}</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span>Term:</span>
                    <span>{listing.desiredTermMonths} months</span>
                  </div>
                  <div className="jo-xyz-demo-summary-item">
                    <span>Estimated Monthly Payment:</span>
                    <span>{formatCurrency(calculateMonthlyPayment(commitAmount || 0, 8.5, listing.desiredTermMonths))}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="jo-xyz-demo-modal-footer">
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
                onClick={() => setShowCommitModal(false)}
              >
                Cancel
              </button>
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={handleCommit}
                disabled={!commitAmount || parseFloat(commitAmount) <= 0 || parseFloat(commitAmount) > remainingAmount}
              >
                Commit Investment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetDetail;
